'use client';
import { useId, useState, useRef, useEffect, type FormEvent, useCallback } from 'react';
import Image from 'next/image';
import { Icon } from 'components/UI/Icon';
import { Input } from 'components/UI/Input';
import { Select } from 'components/UI/Select';
import boysImage from './bmi-boys.webp';
import styles from './bmi-children.module.scss';
import girlsImage from './bmi-girls.webp';
import { updateMarkerPosition, type MarkerPosition } from './updateMarkerPosition';

function calculateBmi(heightCm: number, weightKg: number): number {
  const m = heightCm / 100;
  return weightKg > 0 && m > 0 ? weightKg / (m * m) : NaN;
}

const generateAgeOptions = () => {
  const options = [];

  for (let i = 0.5; i <= 5.5; i += 0.5) {
    const value = i.toString();
    const formattedAge = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(i);
    const label = i === 1 ? '1 Jahr' : `${formattedAge} Jahre`;
    options.push({ value, label });
  }

  for (let i = 6; i <= 18; i++) {
    const value = i.toString();
    const label = `${i} Jahre`;
    options.push({ value, label });
  }

  return options;
};

const AGE_OPTIONS = generateAgeOptions();

const GENDER_OPTIONS = [
  { value: 'Mädchen', label: 'Weiblich' },
  { value: 'Junge', label: 'Männlich' },
];

export const WidgetToolsCalculatorBmiChildren = () => {
  const componentRef = useRef<HTMLDivElement>(null);

  const ageId = useId();
  const genderId = useId();
  const heightId = useId();
  const weightId = useId();

  const [age, setAge] = useState('0.5');
  const [gender, setGender] = useState<'Mädchen' | 'Junge'>('Mädchen');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [bmiValue, setBmiValue] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [markerPosition, setMarkerPosition] = useState<MarkerPosition>({
    bottom: 0,
    left: 0,
  });

  const [submittedValues, setSubmittedValues] = useState<{
    age: string;
    gender: 'Mädchen' | 'Junge';
    height: string;
    weight: string;
  } | null>(null);

  const imageRef = useRef<HTMLImageElement | null>(null);
  const markerRef = useRef<HTMLDivElement | null>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const heightNum = parseFloat(height);
    const weightNum = parseFloat(weight);

    if (heightNum > 0 && weightNum > 0) {
      const bmi = calculateBmi(heightNum, weightNum);
      if (!Number.isNaN(bmi) && Number.isFinite(bmi)) {
        setBmiValue(bmi);
        setSubmittedValues({ age, gender, height, weight });
        setShowResult(true);

        setTimeout(() => {
          updateMarkerPosition(parseFloat(age), bmi, imageRef, markerRef, setMarkerPosition);
        }, 0);
      }
    }
  }

  const reset = useCallback(() => {
    setAge('0.5');
    setGender('Mädchen');
    setHeight('');
    setWeight('');
    setBmiValue(0);
    setSubmittedValues(null);
    setShowResult(false);
    setMarkerPosition({ bottom: 0, left: 0 });

    componentRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (showResult && bmiValue && submittedValues) {
        updateMarkerPosition(parseFloat(submittedValues.age), bmiValue, imageRef, markerRef, setMarkerPosition);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [submittedValues, bmiValue, showResult]);

  return (
    <div ref={componentRef} className={styles.root}>
      <form onSubmit={onSubmit}>
        <div className={styles.fieldWrapper}>
          <Select
            id={ageId}
            label="Alter"
            options={AGE_OPTIONS}
            value={age}
            onChange={(event) => {
              setAge(event.target.value);
            }}
            required
          />

          <Select
            id={genderId}
            label="Geschlecht"
            options={GENDER_OPTIONS}
            value={gender}
            onChange={(event) => {
              setGender(event.target.value as 'Mädchen' | 'Junge');
            }}
            required
          />

          <Input
            id={heightId}
            type="number"
            label="Körpergröße"
            suffix="cm"
            inputMode="numeric"
            value={height}
            onChange={(event) => {
              setHeight(event.target.value);
            }}
            min={1}
            max={999}
            required
          />

          <Input
            id={weightId}
            type="number"
            label="Körpergewicht"
            suffix="kg"
            inputMode="decimal"
            step="0.5"
            value={weight}
            onChange={(event) => {
              setWeight(event.target.value);
            }}
            min={1}
            max={999}
            required
          />
        </div>

        <button className={styles.button} type="submit">
          Auswerten
        </button>
      </form>

      {showResult && submittedValues && (
        <div className={styles.results} aria-live="polite">
          <div className={styles.headline}>
            Der Body Mass Index beträgt:{' '}
            {new Intl.NumberFormat('de-DE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(bmiValue)}
          </div>

          <p className={styles.resultExplanation}>
            Bei der Einordnung des BMIs hilft euch das{' '}
            <Icon className={styles.resultMarkerIcon} name="marker" title="X" /> in der Grafik unten und die
            Beschreibung darunter.
          </p>

          <p>Das waren eure Angaben:</p>
          <ul className={styles.resultList}>
            <li>
              {submittedValues.gender},{' '}
              {(() => {
                const ageValue = parseFloat(submittedValues.age);
                const ageFormatter = new Intl.NumberFormat('de-DE', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                });
                const formattedAge = ageFormatter.format(ageValue);
                return ageValue === 1 ? '1 Jahr' : `${formattedAge} Jahre`;
              })()}
            </li>
            <li>
              Körpergröße:{' '}
              {new Intl.NumberFormat('de-DE', {
                style: 'unit',
                unit: 'centimeter',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(parseFloat(submittedValues.height))}
            </li>
            <li>
              Körpergewicht:{' '}
              {new Intl.NumberFormat('de-DE', {
                style: 'unit',
                unit: 'kilogram',
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
              }).format(parseFloat(submittedValues.weight))}
            </li>
          </ul>

          <div className={styles.bmiGraph}>
            <Image
              ref={imageRef}
              className={styles.image}
              src={submittedValues.gender === 'Mädchen' ? girlsImage : boysImage}
              alt={`BMI-Grafik für ${submittedValues.gender}`}
              unoptimized
              onLoad={() => {
                updateMarkerPosition(parseFloat(submittedValues.age), bmiValue, imageRef, markerRef, setMarkerPosition);
              }}
            />
            <div
              ref={markerRef}
              className={styles.marker}
              style={{
                bottom: `${markerPosition.bottom}px`,
                left: `${markerPosition.left}px`,
              }}
            >
              <Icon className={styles.markerIcon} name="marker" title="Marker" size={20} />
            </div>
          </div>

          <div className={styles.description}>
            <div className={styles.subheadline}>Untergewicht</div>
            <p>
              Laut Berechnung des BMIs hat euer Kind Untergewicht. In den Wachstumsphasen kann der BMI immer mal
              schwanken. Solange sich euer Kind aber normal entwickelt und gesund ist, braucht ihr euch keine Gedanken
              machen. Dennoch ist es wichtig, dass ihr das Gewicht eures Kindes im Auge behaltet.
            </p>
            <p>
              Wenn ihr unsicher seid, wendet euch an euren Kinderarzt, um Krankheiten auszuschließen. Grundsätzlich
              sollten täglich Obst und Gemüse auf dem Speiseplan stehen, ebenso wie fettarme Milchprodukte. Achtet
              darauf, dass euer Kind regelmäßig Fleisch und Fisch isst. Wenn euer Kind grundsätzlich sehr wenig oder
              unregelmäßig isst, könnt ihr ebenfalls euren Kinderarzt um Rat bitten. Im Zweifel kann auch ein
              Ernährungsberater hinzugezogen werden.
            </p>
            <p>
              Wenn ihr den Verdacht habt, dass euer Kind magersüchtig ist, wendet euch bitte unbedingt an einen Arzt.
            </p>
          </div>

          <div className={styles.description}>
            <div className={styles.subheadline}>Normalgewicht</div>
            <p>
              Euer Kind hat laut Berechnung des BMIs Normalgewicht. Ihr braucht euch also keinerlei Sorgen zu machen.
              Wichtig für Kinder ist grundsätzlich eine ausgewogene Ernährung mit einer ausreichenden Menge an Vitaminen
              und Mineralstoffen. Diese stecken vor allem in frischem Obst und Gemüse. Auch Fleisch und Fisch sollten
              regelmäßig auf dem Speiseplan stehen. Die tägliche Versorgung mit möglichst fettarmen Milchprodukten
              gewährleistet eine gute Versorgung mit Kalzium, das wichtig ist für den Knochenbau und das Wachstum. Sorgt
              dafür, dass Süßigkeiten, süße Getränke wie Limonaden und ähnliches eher die Ausnahme bleiben.
            </p>
            <p>
              Wenn sich euer Kind noch dazu regelmäßig bewegt und viel an die frische Luft kommt, ist alles in bester
              Ordnung. Auf diese Weise beugt ihr Krankheiten, Übergewicht und auch Entwicklungsstörungen am besten vor.
            </p>
          </div>

          <div className={styles.description}>
            <div className={styles.subheadline}>Übergewicht</div>
            <p>
              Laut Berechnung des BMIs leidet euer Kind an Übergewicht. In Wachstumsphasen kann der BMI eures Kindes
              durchaus schwanken. Liegen keine zusätzlichen Erkrankungen vor, braucht ihr euch keine Sorgen zu machen.
              Dennoch ist es wichtig, dass ihr das Gewicht eures Kindes im Auge behaltet.
            </p>
            <p>
              Mit einer gesunden, ausgewogenen Ernährung könnt ihr Übergewicht am besten vorbeugen. Auf den Speiseplan
              eures Kindes gehören täglich frisches Obst und Gemüse sowie fettarme Milchprodukte. Auch Fleisch und Fisch
              sollte euer Kind regelmäßig essen. Achtet darauf, dass euer Kind nur in Ausnahmefällen Süßigkeiten und
              süße Getränke wie Limonaden und Säfte zu sich nimmt. Gebt eurem Kind statt Saft besser Saftschorlen oder
              ungesüßte Tees zu trinken. Achtet darauf, dass ihr euer Kind grundsätzlich fettarm ernährt. Greift also
              anstelle von Vollfett- öfter zu Halbfett-Produkten.
            </p>
            <p>
              Regelmäßige Bewegung tut eurem Kind gut. Auf dem Spielplatz zu toben oder Sport zu treiben, macht Spaß,
              verbrennt Kalorien und lenkt vom Essen ab. Zudem fördert die Bewegung die geistige und motorische
              Entwicklung eures Kindes.
            </p>
          </div>

          <div className={styles.description}>
            <div className={styles.subheadline}>Starkes Übergewicht/Adipositas</div>
            <p>
              Laut Berechnung des BMIs leidet euer Kind unter starkem Übergewicht. Ärzte sprechen in diesem Fall auch
              von Fettleibigkeit oder Adipositas. Nehmt mit eurem Kinderarzt Kontakt auf. Gegebenenfalls könnt ihr ein
              Ernährungsprogramm für euer Kind in Angriff nehmen oder speziell dafür ausgebildete Ernährungsberater
              können eurem Kind beim Abnehmen helfen. Sie erklären euch, mit welchen Maßnahmen ihr euer Kind optimal
              beim Abnehmen unterstützen könnt. Ohne eine Anleitung ist es schwierig, die richtigen und notwendigen
              Maßnahmen konsequent einzuhalten und umzusetzen.
            </p>
            <p>
              Es ist wichtig, Übergewicht schon im Kindesalter zu bekämpfen. Starkes Übergewicht schränkt die
              Lebensqualität und auch die Bewegungsfreiheit eures Kindes stark ein. Das wiederum führt dazu, dass sich
              euer Kind noch weniger bewegt und sich eventuell sogar von anderen Kindern isoliert. Außerdem kann starkes
              Übergewicht weitreichende gesundheitliche Probleme zur Folge haben.
            </p>
            <p>
              Motiviert euer Kind, sich zu bewegen. Geht mit ihm auf den Spielplatz oder zum Kinderturnen. So wird
              Bewegung schon früh normal für euer Kind. Außerdem fördert sie die Motorik, die geistige Entwicklung und
              beugt nicht zuletzt Übergewicht vor.
            </p>
          </div>

          <button className={styles.button} type="button" onClick={reset}>
            Neu berechnen
          </button>
        </div>
      )}
    </div>
  );
};
